import type { ReactNode } from 'react';
import clsx from 'clsx';
import { useEffect } from 'react';
import Portal from '@/shared/ui/Portal';

type ModalOverlayTone = 'dimmed' | 'dark';
type ModalPanelVariant = 'card' | 'bare';

interface ModalProps {
  children: ReactNode;
  onClose: () => void;
  /** Applied to the inner panel wrapper. */
  className?: string;
  overlayClassName?: string;
  overlayTone?: ModalOverlayTone;
  panelVariant?: ModalPanelVariant;
  ariaLabelledBy?: string;
  ariaLabel?: string;
  closeOnOverlayClick?: boolean;
  /** When true, Escape does not call onClose (use while a stacked modal is open). */
  suppressEscapeClose?: boolean;
  /** Tailwind z-index class; use a higher value for modals opened above another modal. */
  zIndexClassName?: string;
}

const overlayToneClassNames: Record<ModalOverlayTone, string> = {
  dimmed: 'bg-black/50',
  dark: 'bg-black/90',
};

const panelVariantClassNames: Record<ModalPanelVariant, string> = {
  card: 'overflow-hidden rounded-xl bg-white shadow-xl',
  bare: '',
};

export default function Modal({
  children,
  onClose,
  className,
  overlayClassName,
  overlayTone = 'dimmed',
  panelVariant = 'card',
  ariaLabelledBy,
  ariaLabel,
  closeOnOverlayClick = true,
  suppressEscapeClose = false,
  zIndexClassName = 'z-50',
}: ModalProps) {
  useEffect(() => {
    if (suppressEscapeClose) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, suppressEscapeClose]);

  return (
    <Portal>
      <div
        className={clsx(
          'fixed inset-0 flex items-center justify-center p-4',
          zIndexClassName,
          overlayToneClassNames[overlayTone],
          overlayClassName,
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabelledBy}
        aria-label={ariaLabel}
        onClick={closeOnOverlayClick ? onClose : undefined}
      >
        <div
          className={clsx('w-full', panelVariantClassNames[panelVariant], className)}
          onClick={(event) => event.stopPropagation()}
        >
          {children}
        </div>
      </div>
    </Portal>
  );
}
